// Used in teacher.component.html

// Angular
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import { Router } from "@angular/router";

// RXJS/Store
import * as fromCreateStore from "#components/content-library/assign-cart/content-builder-store";
import * as RouterStore from "#core/router-store";
import * as fromCourseStore from "#core/teacher-classes/store";
import { Store, select } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as fromCartStore from "../../assign-cart-store";

// Services/Types/Helpers
import { LocalStorage } from "#services/local-storage.service";
import { EditionId, SubjectId } from "@carnegie-learning/common-types";
import { assetTypeIds } from "@carnegie-learning/cure-types";
import { SelectedAssets } from "cli-types";

@Component({
    selector: "assign-cart-tab",
    templateUrl: "./assign-cart-tab.component.html",
    styleUrls: ["./assign-cart-tab.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AssignCartTabComponent implements OnInit, OnDestroy {
    assignCartTabOpen = false;
    cartItems: SelectedAssets = { editionId: "", assets: [] };
    creatingLesson = false;
    creatingActivity = false;
    showAssignTab = false;
    pageInit = false;
    subjectId: SubjectId = "";
    editionId: EditionId = "";

    private ngUnsubscribe = new Subject();

    @Output() sidePanelRightToggle = new EventEmitter<void>();

    constructor(
        private cartStore: Store<fromCartStore.CartState>,
        private createStore: Store<fromCreateStore.CreateState>,
        private cdr: ChangeDetectorRef,
        private localStorage: LocalStorage,
        private router: Router,
        private routerStore: Store<RouterStore.RouterStateUrl>,
        private courseStore: Store<fromCourseStore.CoursesState>
    ) {}

    ngOnInit() {
        this.pageInit = true;
        const selectedAssets: SelectedAssets | undefined = JSON.parse(
            this.localStorage.get("selectedAssets")
        );
        if (selectedAssets?.assets?.length) {
            this.createStore.dispatch(
                fromCreateStore.SetSelectedAssets({ payload: selectedAssets })
            );
            if (!this.router.url.includes("review")) {
                this.cartStore.dispatch(
                    fromCartStore.setAssignCartTabOpen({ payload: true })
                );
            }
        }

        this.createStore
            .pipe(select(fromCreateStore.getSelectedAssets))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedAssetsFromStore: SelectedAssets) => {
                if (
                    selectedAssetsFromStore &&
                    selectedAssetsFromStore.assets.length > 0
                ) {
                    // If 1 or more selected asset, show Assign tab
                    this.showAssignTab = true;
                    this.cartItems = selectedAssetsFromStore;
                    const containsComposite =
                        selectedAssetsFromStore.assets.find(
                            (a) => a.assetTypeId === assetTypeIds.BUNDLE
                        );
                    if (containsComposite) {
                        this.cartStore.dispatch(
                            fromCartStore.setContainsComposite({
                                payload: true,
                            })
                        );
                    }
                } else if (
                    selectedAssetsFromStore &&
                    selectedAssetsFromStore.assets.length < 1 &&
                    this.creatingLesson
                ) {
                    // If creating a lesson and no assets selected, initialize cart items
                    this.cartItems = { ...this.cartItems, assets: [] };
                } else {
                    this.cartStore.dispatch(
                        fromCartStore.setContainsComposite({ payload: false })
                    );
                    this.cartItems = { ...this.cartItems, assets: [] };
                }
                // set selectedAssets in local storage
                if (!this.pageInit && selectedAssetsFromStore?.assets) {
                    this.localStorage.set(
                        "selectedAssets",
                        JSON.stringify(selectedAssetsFromStore)
                    );
                } else {
                    this.pageInit = false;
                }
                this.cdr.detectChanges();
            });

        this.routerStore
            .pipe(select(RouterStore.getRouterState))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((router) => {
                if (router) {
                    const editionId = router.state.params.editionId;
                    if (editionId && this.editionId !== editionId) {
                        this.editionId = editionId;
                        this.setProgramEditionId(this.editionId);
                    } else if (
                        !editionId &&
                        this.cartItems.assets.length &&
                        this.editionId !== this.cartItems.editionId
                    ) {
                        this.editionId = this.cartItems.editionId;
                        this.setProgramEditionId(this.editionId);
                    } else if (
                        (!editionId && !this.cartItems) ||
                        !this.cartItems.assets.length
                    ) {
                        this.courseStore
                            .pipe(
                                select(fromCourseStore.getSelectedCourseObject)
                            )
                            .pipe(takeUntil(this.ngUnsubscribe))
                            .subscribe((course) => {
                                if (
                                    course !== undefined &&
                                    course != null &&
                                    this.editionId !== course.editionId
                                ) {
                                    this.editionId = course.editionId;
                                    this.setProgramEditionId(this.editionId);
                                }
                            });
                    }

                    if (
                        (!router.state.url.includes("review") ||
                            router.state.url.includes("preview")) &&
                        this.cartItems &&
                        this.cartItems.assets.length > 0
                    ) {
                        this.assignCartTabOpen = true;
                        this.cartStore.dispatch(
                            fromCartStore.setAssignCartTabOpen({
                                payload: true,
                            })
                        );
                    }
                }
            });

        this.cartStore
            .pipe(select(fromCartStore.getAssignCartTabOpen))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response: boolean) => {
                this.assignCartTabOpen = response;
                this.cdr.detectChanges();
            });

        this.cartStore
            .pipe(select(fromCartStore.getCreatingLesson))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response: boolean) => {
                this.creatingLesson = response;
                this.cdr.detectChanges();
            });
    }

    // Get Cart Items count for badge
    getCartItems(cartItems: SelectedAssets | undefined) {
        if (cartItems && cartItems.assets.length > 0) {
            return cartItems.assets.length;
        }
    }

    // Open the Assign Cart side panel
    openAssignSidePanel() {
        this.cartStore.dispatch(
            fromCartStore.setAssignCartTabOpen({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setAssignCartOpen({ payload: true })
        );
    }

    setProgramSubjectId(subjectId: SubjectId) {
        this.cartStore.dispatch(
            fromCartStore.setProgramSubjectId({ payload: subjectId })
        );
    }

    setProgramEditionId(editionId: EditionId) {
        if (editionId) {
            this.cartStore.dispatch(
                fromCartStore.setProgramEditionId({ payload: editionId })
            );
        } else {
            console.error("editionId unknown");
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
